import type { Store } from '@reduxjs/toolkit';
import { castDraft, produce } from 'immer';
import _isEqual from 'lodash/isEqual';
import React, { useContext } from 'react';
import { createContext, useState, useSyncExternalStore } from 'react';
import { useStore } from 'react-redux';
import type { TaggedUnion } from 'type-fest';

export enum ValidationTrigger {
  onChange = 1,
  onBlur = 1 << 1,
  onSubmit = 1 << 2,
}

type FormState<S, F extends string, ExtraKeys extends Partial<Record<F, string | number>>> = {
  state: S;
  errors: {
    [field in F]: Record<
      string,
      ExtraKeys extends { [f in field]: string | number }
        ? Record<ExtraKeys[field], string | undefined>
        : string | undefined
    >;
  };
  submitted: boolean;
  submitting: boolean;
  successfullySubmitted: boolean;
};

export type ChangeAction<F extends string, Values extends Record<F, unknown>, ExtraKeys extends Record<F, unknown>> = {
  t: 'valueChange';
} & TaggedUnion<
  'field',
  {
    [key in F]: {
      value: Values[key];
    } & (ExtraKeys extends { [f in key]: string | number } ? { key: ExtraKeys[key] } : { key?: undefined });
  }
>;

export type BlurAction<F extends string, ExtraKeys extends Record<F, unknown>> = {
  t: 'fieldBlurred';
} & TaggedUnion<
  'field',
  {
    [key in F]: ExtraKeys extends { [f in key]: string | number } ? { key: ExtraKeys[key] } : { key?: undefined };
  }
>;

type FormAction<A, F extends string, Values extends Record<F, unknown>, ExtraKeys extends Record<F, unknown>> =
  | TaggedUnion<
      't',
      {
        submitted: { x?: unknown };
        startedSubmitting: { x?: unknown };
        finishedSubmitting: { x?: unknown };
        raw: { action: A };
      }
    >
  | ChangeAction<F, Values, ExtraKeys>
  | BlurAction<F, ExtraKeys>;

type FieldConfigs<
  S,
  F extends string,
  Values extends Record<F, unknown>,
  ReduxState,
  ExtraKeys extends Partial<Record<F, string | number>>,
> = {
  [field in F]: ExtraKeys extends { [f in field]: string | number }
    ? {
        selector: (state: S, key: ExtraKeys[field]) => Values[field];
        selectActiveKeys: (state: S) => Array<ExtraKeys[field]>;
        validation?: Record<
          string,
          {
            triggers: number;
            validate: (state: S, store: ReduxState, key: ExtraKeys[field]) => string | undefined;
          }
        >;
        disabledSelector?: (state: S, key: ExtraKeys[field]) => boolean;
      }
    : {
        selector: (state: S) => Values[field];
        validation?: Record<
          string,
          {
            triggers: number;
            validate: (state: S, store: ReduxState) => string | undefined;
          }
        >;
        disabledSelector?: (state: S) => boolean;
      };
};

/**
 * @deprecated use formBuilder instead
 */
const createForm = <
  S extends object,
  ReduxState extends object,
  Values extends object,
  F extends Extract<keyof Values, string>,
  ExtraKeys extends Partial<Record<F, string | number>>,
  C extends FieldConfigs<S, F, Values, ReduxState, ExtraKeys>,
  I,
  Submit = S,
  AdditionalActions = { t: never },
>({
  reducer,
  fieldConfigs,
  init,
  submitTransformer,
}: {
  reducer: (
    state: S,
    action: AdditionalActions | ChangeAction<F, Values, ExtraKeys> | BlurAction<F, ExtraKeys>,
    store: ReduxState,
  ) => S;
  init: (input: I, store: ReduxState) => S;
  fieldConfigs: C;
  submitTransformer: (state: S) => Submit;
  types: {
    // Just for type inference
    reduxStore: ReduxState;
    fieldValues: Values;
    extraKeys?: ExtraKeys;
    additionalActions?: AdditionalActions;
  };
}) => {
  return {
    selectors: {
      isValid: (state: FormState<S, F, ExtraKeys>): boolean =>
        Object.keys(fieldConfigs).every((key) => {
          const fieldConfig = fieldConfigs[key as F];
          if (!state.errors[key as F]) return true;
          if ('selectActiveKeys' in fieldConfig) {
            type KeyType = ReturnType<typeof fieldConfig.selectActiveKeys>[number];
            const fieldErrors = state.errors[key as F] as Record<string, Partial<Record<KeyType, string | undefined>>>;
            // We only want to check for the fields that are currently active
            const fieldKeys = fieldConfig.selectActiveKeys(state.state);
            // Iterate through each possible type of error
            return Object.values(fieldErrors).every((fieldError) =>
              fieldKeys.every((fieldKey) => !fieldError || !fieldError[fieldKey as KeyType]),
            );
          } else {
            // If there aren't any keys to use then we can just check for top level errors
            const fieldError = state.errors[key as F] as Record<string, string | undefined>;
            return Object.values(fieldError).every((error) => !error);
          }
        }),
      helperText: <
        Field extends F,
        HasKey extends boolean = ExtraKeys extends { [f in Field]: string | number } ? true : false,
      >(
        ...args: HasKey extends true
          ? [FormState<S, F, ExtraKeys>, Field, ExtraKeys[Field]]
          : [FormState<S, F, ExtraKeys>, Field]
      ): string | undefined => {
        for (const err of Object.values<string | undefined | Record<NonNullable<ExtraKeys[Field]>, string | undefined>>(
          args[0].errors[args[1]],
        )) {
          if (typeof err === 'string') {
            return err;
          } else if (err && typeof err === 'object') {
            if (args[2] != undefined && err[args[2]]) {
              return err[args[2]];
            }
          }
        }
        return undefined;
      },
      isDirty: (state: FormState<S, F, ExtraKeys>, initial: S) => !_isEqual(state.state, initial),
      isSubmitting: (state: FormState<S, F, ExtraKeys>) => state.submitting,
      isSuccessfullySubmitted: (state: FormState<S, F, ExtraKeys>) => state.successfullySubmitted,
      fieldValue: <
        Field extends F,
        HasKey extends boolean = ExtraKeys extends { [f in Field]: string | number } ? true : false,
      >(
        ...args: HasKey extends true
          ? [FormState<S, F, ExtraKeys>, Field, ExtraKeys[Field]]
          : [FormState<S, F, ExtraKeys>, Field]
      ): Values[Field] => {
        const fieldConfig = fieldConfigs[args[1]];
        if (args[2] !== undefined && 'selectActiveKeys' in fieldConfig) {
          return fieldConfig.selector(args[0].state, args[2]) as Values[Field];
        } else if (args[2] == undefined && !('selectActiveKeys' in fieldConfig)) {
          return fieldConfig.selector(args[0].state) as Values[Field];
        }
        throw new Error(`Trying to access a keyed value without a key: (${JSON.stringify(args)})`);
      },
      isDisabled: <
        Field extends F,
        HasKey extends boolean = ExtraKeys extends { [f in Field]: string | number } ? true : false,
      >(
        ...args: HasKey extends true
          ? [FormState<S, F, ExtraKeys>, Field, ExtraKeys[Field]]
          : [FormState<S, F, ExtraKeys>, Field]
      ): boolean => {
        const fieldConfig = fieldConfigs[args[1]];
        if (args[2] !== undefined && 'selectActiveKeys' in fieldConfig) {
          return fieldConfig.disabledSelector?.(args[0].state, args[2]) ?? false;
        } else if (args[2] == undefined && !('selectActiveKeys' in fieldConfig)) {
          return fieldConfig.disabledSelector?.(args[0].state) ?? false;
        }
        throw new Error(`Trying to access a keyed value without a key: (${JSON.stringify(args)})`);
      },
    },
    init: (initial: I, store: ReduxState): FormState<S, F, ExtraKeys> => {
      return {
        state: init(initial, store),
        errors: Object.keys(fieldConfigs).reduce(
          (errors, fieldName) => {
            errors[fieldName as F] = {};
            return errors;
          },
          {} as {
            [field in F]: Record<
              string,
              ExtraKeys extends { [f in field]: string | number }
                ? Record<ExtraKeys[field], string | undefined>
                : string | undefined
            >;
          },
        ),
        submitted: false,
        submitting: false,
        successfullySubmitted: false,
      };
    },
    reducer: (
      formState: FormState<S, F, ExtraKeys>,
      action: FormAction<AdditionalActions, F, Values, ExtraKeys>,
      store: ReduxState,
    ): FormState<S, F, ExtraKeys> => {
      switch (action.t) {
        case 'valueChange': {
          return produce(formState, (draft) => {
            draft.state = castDraft(reducer(formState.state, action, store));
            const errors = draft.errors as FormState<S, F, ExtraKeys>['errors'];
            for (const field of Object.keys(fieldConfigs)) {
              const config = fieldConfigs[field as F];
              if (config.validation) {
                for (const validationKey of Object.keys(config.validation)) {
                  if (formState.submitted || config.validation[validationKey].triggers & ValidationTrigger.onChange) {
                    if ('selectActiveKeys' in config) {
                      const errorRecord = errors[field as F] as Record<
                        string,
                        Partial<Record<ReturnType<typeof config.selectActiveKeys>[number], string | undefined>>
                      >;
                      const keys = config.selectActiveKeys(draft.state as S);
                      if (!errorRecord[validationKey]) {
                        errorRecord[validationKey] = {};
                      }
                      for (const key of keys) {
                        errorRecord[validationKey][key] = config.validation[validationKey].validate(
                          draft.state as S,
                          store,
                          key,
                        );
                      }
                    } else {
                      const errorRecord = errors[field as F] as Record<string, string | undefined>;
                      errorRecord[validationKey] = config.validation[validationKey].validate(draft.state as S, store);
                    }
                  }
                }
              }
            }
          });
        }
        case 'submitted': {
          return produce(formState, (draft) => {
            draft.submitted = true;
            const errors = draft.errors as FormState<S, F, ExtraKeys>['errors'];
            for (const field of Object.keys(fieldConfigs)) {
              const config = fieldConfigs[field as F];
              if (config.validation) {
                for (const validationKey of Object.keys(config.validation)) {
                  if ('selectActiveKeys' in config) {
                    const validation = config.validation[validationKey];
                    const errorRecord = errors[field as F] as Record<
                      string,
                      Partial<Record<ReturnType<typeof config.selectActiveKeys>[number], string | undefined>>
                    >;
                    const keys = config.selectActiveKeys(formState.state);
                    if (!errorRecord[validationKey]) {
                      errorRecord[validationKey] = {};
                    }
                    for (const key of keys) {
                      errorRecord[validationKey][key] = validation.validate(formState.state, store, key);
                    }
                  } else {
                    const validation = config.validation[validationKey];
                    const errorRecord = errors[field as F] as Record<string, string | undefined>;
                    errorRecord[validationKey] = validation.validate(formState.state, store);
                  }
                }
              }
            }
          });
        }
        case 'fieldBlurred': {
          return produce(formState, (draft) => {
            draft.state = castDraft(reducer(formState.state, action, store));
            const config = fieldConfigs[action.field];
            if (config.validation) {
              for (const validationKey of Object.keys(config.validation)) {
                if (config.validation[validationKey].triggers & ValidationTrigger.onBlur) {
                  const errors = draft.errors as FormState<S, typeof action.field, ExtraKeys>['errors'];
                  if ('selectActiveKeys' in config) {
                    const errorRecord = errors[action.field as F] as Record<
                      string,
                      Partial<Record<ReturnType<typeof config.selectActiveKeys>[number], string | undefined>>
                    >;
                    if (!action.key) {
                      // Should not be possible, just making ts happy
                      throw new Error('Trying to update a field without a proper key');
                    }
                    if (!errorRecord[validationKey]) {
                      errorRecord[validationKey] = {};
                    }
                    errorRecord[validationKey][action.key] = config.validation[validationKey].validate(
                      draft.state as S,
                      store,
                      action.key,
                    );
                  } else {
                    const errorRecord = errors[action.field] as Record<string, string | undefined>;
                    errorRecord[validationKey] = config.validation[validationKey].validate(formState.state, store);
                  }
                }
              }
            }
          });
        }
        case 'startedSubmitting':
          return produce(formState, (draft) => {
            draft.submitting = true;
          });
        case 'finishedSubmitting':
          return produce(formState, (draft) => {
            draft.submitting = false;
            draft.successfullySubmitted = true;
          });
        case 'raw':
          return produce(formState, (draft) => {
            draft.state = castDraft(reducer(formState.state, action.action, store));
            const errors = draft.errors as FormState<S, F, ExtraKeys>['errors'];
            for (const field of Object.keys(fieldConfigs)) {
              const config = fieldConfigs[field as F];
              if (config.validation) {
                for (const validationKey of Object.keys(config.validation)) {
                  if (formState.submitted || config.validation[validationKey].triggers & ValidationTrigger.onChange) {
                    if ('selectActiveKeys' in config) {
                      const validation = config.validation[validationKey];
                      const errorRecord = errors[field as F] as Record<
                        string,
                        Partial<Record<ReturnType<typeof config.selectActiveKeys>[number], string | undefined>>
                      >;
                      const keys = config.selectActiveKeys(draft.state as S);
                      if (!errorRecord[validationKey]) {
                        errorRecord[validationKey] = {};
                      }
                      for (const key of keys) {
                        errorRecord[validationKey][key] = validation.validate(draft.state as S, store, key);
                      }
                    } else {
                      const validation = config.validation[validationKey];
                      const errorRecord = errors[field as F] as Record<string, string | undefined>;
                      errorRecord[validationKey] = validation.validate(draft.state as S, store);
                    }
                  }
                }
              }
            }
          });
      }
    },
    createManager(store: Store<ReduxState>, input: I) {
      let state = this.init(input, store.getState());
      const initialState = state.state;
      const subs = new Set<() => void>();
      const dispatch = (action: FormAction<AdditionalActions, F, Values, ExtraKeys>) => {
        state = this.reducer(state, action, store.getState());
        for (const sub of subs.values()) {
          sub();
        }
      };
      const subscribe = (cb: () => void) => {
        const reduxUnsub = store.subscribe(cb);
        subs.add(cb);
        return () => {
          subs.delete(cb);
          reduxUnsub();
        };
      };
      return {
        formDispatch: (action: AdditionalActions) => {
          dispatch({ t: 'raw', action });
        },
        subscribe,
        dispatch,
        submit: async (cb: (payload: Submit) => Promise<void> | void) => {
          dispatch({ t: 'submitted' });
          if (this.selectors.isValid(state)) {
            dispatch({ t: 'startedSubmitting' });
            await new Promise(requestAnimationFrame);
            try {
              await cb(submitTransformer(state.state));
            } finally {
              dispatch({ t: 'finishedSubmitting' });
            }
          }
        },
        initialState,
        get state() {
          // Useful for testing
          return state;
        },
      };
    },
    createManagerProvider() {
      type Manager = ReturnType<typeof this.createManager>;
      const context = createContext<Manager | undefined>(undefined);
      const useManager = () => {
        const manager = useContext(context);
        if (!manager) {
          throw new Error('Trying to use a form manager outside of a form context');
        }
        return manager;
      };
      return {
        Provider: ({ input, children }: React.PropsWithChildren<{ input: I }>) => {
          const redux = useStore<ReduxState>();
          const [manager] = useState(this.createManager(redux, input));

          return React.createElement(context.Provider, { value: manager }, children);
        },
        context,
        useManager,
        useSelector: <Result>(selector: (store: ReduxState, state: S) => Result): Result => {
          const manager = useManager();
          const reduxStore = useStore<ReduxState>();
          return useSyncExternalStore(useManager().subscribe, () =>
            selector(reduxStore.getState(), manager.state.state),
          );
        },
        useFormField: <Field extends F>(
          // Super hacky way to tell typescript that we don't need a second argument if the type is void.
          // For some reason typescript can't figure that out if there is a generic involved
          ...args: ExtraKeys extends { [f in Field]: string | number } ? [Field, ExtraKeys[Field]] : [Field]
        ) => {
          const manager = useManager();
          const field = args[0];
          const key = args[1] as ExtraKeys[Field];
          return {
            onChange: (e: { target: { value: string } }) =>
              manager.dispatch({ t: 'valueChange', field, value: e.target.value as unknown, key } as FormAction<
                AdditionalActions,
                F,
                Values,
                ExtraKeys
              >),
            onValueChange: (value: Values[typeof field]) =>
              manager.dispatch({ t: 'valueChange', field, value, key } as FormAction<
                AdditionalActions,
                F,
                Values,
                ExtraKeys
              >),
            onBlur: () =>
              manager.dispatch({ t: 'fieldBlurred', field, key } as FormAction<
                AdditionalActions,
                F,
                Values,
                ExtraKeys
              >),
            value: useSyncExternalStore(manager.subscribe, () =>
              this.selectors.fieldValue<Field, true>(manager.state, field, key),
            ),
            disabled: useSyncExternalStore(manager.subscribe, () =>
              this.selectors.isDisabled<Field, true>(manager.state, field, key),
            ),
            helperText: useSyncExternalStore(manager.subscribe, () =>
              this.selectors.helperText<Field, true>(manager.state, field, key),
            ),
          };
        },
        useIsDirty: () => {
          const manager = useManager();
          return useSyncExternalStore(manager.subscribe, () =>
            this.selectors.isDirty(manager.state, manager.initialState),
          );
        },
        useIsSubmitting: () => {
          const manager = useManager();
          return useSyncExternalStore(manager.subscribe, () => this.selectors.isSubmitting(manager.state));
        },
        useIsSuccessfullySubmitted: () => {
          const manager = useManager();
          return useSyncExternalStore(manager.subscribe, () => this.selectors.isSuccessfullySubmitted(manager.state));
        },
        useIsValid: () => {
          const manager = useManager();
          return useSyncExternalStore(manager.subscribe, () => this.selectors.isValid(manager.state));
        },
      };
    },
  };
};

export default createForm;
